import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { vendingTableBodyCellSx, vendingTableHeadCellSx } from './alertDisplayUtils';
import vendingApiClient from '../../Services/VendingApi';
import { COLORS } from '../../Styles/Colors';
import {
  VendingMachineHistoryEvent,
  VendingMachineHistoryEventType,
  VendingMachineHistoryListResponse,
} from '../../Types/vending';

interface MachineHistoryTabProps {
  machineId: string;
}

type EventFilter = '' | VendingMachineHistoryEventType;

const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const eventTypeMeta = (eventType: VendingMachineHistoryEventType) => {
  switch (eventType) {
    case 'status_change':
      return { label: 'Status', color: COLORS.teal, bg: `${COLORS.teal}14` };
    case 'restock':
      return { label: 'Restock', color: COLORS.orange, bg: `${COLORS.orange}18` };
    default:
      return { label: 'Audit', color: COLORS.darkGray, bg: `${COLORS.lightGray}18` };
  }
};

const formatJson = (value: unknown) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export const MachineHistoryTab = ({ machineId }: MachineHistoryTabProps) => {
  const [events, setEvents] = useState<VendingMachineHistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<EventFilter>('');
  const [detail, setDetail] = useState<VendingMachineHistoryEvent | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendingApiClient(`/machines/${machineId}/history?limit=200`);
      const json: VendingMachineHistoryListResponse = await response.json();
      setEvents(json.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const filteredEvents = useMemo(() => {
    if (!typeFilter) return events;
    return events.filter((event) => event.event_type === typeFilter);
  }, [events, typeFilter]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 2,
          flexShrink: 0,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Event type</InputLabel>
          <Select
            label="Event type"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as EventFilter)}
          >
            <MenuItem value="">All events</MenuItem>
            <MenuItem value="status_change">Status changes</MenuItem>
            <MenuItem value="restock">Restocks</MenuItem>
            <MenuItem value="audit">Audit log</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={loadHistory} disabled={loading} sx={{ ml: 'auto' }}>
          Refresh
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: COLORS.teal }} />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={loadHistory}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && filteredEvents.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            px: 2,
            borderRadius: 2,
            border: `1px dashed ${COLORS.lightGray}`,
            backgroundColor: '#fafbfc',
          }}
        >
          <Typography variant="h6" fontWeight={700} sx={{ color: COLORS.darkGray, mb: 1 }}>
            No history yet
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.lightGray }}>
            Status changes, restocks, and mapping updates for this machine will appear here.
          </Typography>
        </Box>
      )}

      {!loading && !error && filteredEvents.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: `1px solid ${COLORS.lightGray}40`,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader size="medium" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 110 }}>Type</TableCell>
                  <TableCell sx={vendingTableHeadCellSx}>Event</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 140 }}>Actor</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 180 }}>When</TableCell>
                  <TableCell sx={{ ...vendingTableHeadCellSx, width: 90, textAlign: 'right' }}>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEvents.map((event) => {
                  const typeMeta = eventTypeMeta(event.event_type);
                  const hasDetail = event.event_type === 'audit'
                    || Boolean(event.detail)
                    || Boolean(event.meta && Object.keys(event.meta).length);
                  return (
                    <TableRow key={event.id} hover sx={{ '&:hover': { backgroundColor: '#fafbfc' } }}>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Chip
                          size="small"
                          label={typeMeta.label}
                          sx={{ fontWeight: 600, bgcolor: typeMeta.bg, color: typeMeta.color }}
                        />
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Typography variant="body2" fontWeight={600}>{event.title}</Typography>
                        {event.detail && (
                          <Typography variant="caption" sx={{ color: COLORS.lightGray, display: 'block', mt: 0.25 }}>
                            {event.detail}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>{event.actor || '—'}</TableCell>
                      <TableCell sx={vendingTableBodyCellSx}>
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {formatDate(event.occurred_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...vendingTableBodyCellSx, textAlign: 'right' }}>
                        {hasDetail ? (
                          <Button size="small" onClick={() => setDetail(event)}>View</Button>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={detail != null} onClose={() => setDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>{detail?.title || 'Event details'}</DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                <strong>When:</strong> {formatDate(detail.occurred_at)}
              </Typography>
              {detail.actor && (
                <Typography variant="body2">
                  <strong>Actor:</strong> {detail.actor}
                </Typography>
              )}
              {detail.detail && (
                <Typography variant="body2">
                  <strong>Summary:</strong> {detail.detail}
                </Typography>
              )}
              {detail.event_type === 'audit' && (
                <>
                  <Typography variant="subtitle2" fontWeight={700}>Previous value</Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: '#f8f9fa',
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 200,
                    }}
                  >
                    {formatJson(detail.meta.previous_value)}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>New value</Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 2,
                      borderRadius: 1,
                      bgcolor: '#f8f9fa',
                      fontSize: '0.75rem',
                      overflow: 'auto',
                      maxHeight: 200,
                    }}
                  >
                    {formatJson(detail.meta.new_value)}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MachineHistoryTab;
